/**
 * Preview dialog shown before the user confirms sending a diagnostic report.
 *
 * Mirrors the CrashDialog "show what's about to be sent" pattern: renders the
 * redacted payload as JSON so users can verify nothing identifying ships
 * before clicking Send. Cancel closes without firing the IPC.
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import type { DiagnosticsReport } from '../../../types/connectionDiagnostics';
import {
  redactDiagnosticPayload,
  type SendableDiagnosticPayload,
} from '../utils/redactDiagnosticPayload';
import type { DiagnosticsSendState } from './DiagnosticsDialogBody';

type DiagnosticsSendPreviewDialogProps = {
  open: boolean;
  report: DiagnosticsReport | null;
  sendState: DiagnosticsSendState;
  onCancel: () => void;
  onConfirm: () => void;
};

export function DiagnosticsSendPreviewDialog({
  open,
  report,
  sendState,
  onCancel,
  onConfirm,
}: DiagnosticsSendPreviewDialogProps) {
  const [payload, setPayload] = useState<SendableDiagnosticPayload | null>(null);
  const [redactError, setRedactError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !report) {
      setPayload(null);
      setRedactError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const next = await redactDiagnosticPayload(report);
        if (!cancelled) setPayload(next);
      } catch (e) {
        if (!cancelled) {
          const message = e instanceof Error ? e.message : 'Failed to build preview';
          setRedactError(message);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, report]);

  const handleOpenChange = (next: boolean) => {
    if (!next && sendState !== 'sending') onCancel();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="diag-send-preview-dialog">
        <DialogHeader>
          <DialogTitle>Review what will be sent</DialogTitle>
          <DialogDescription>
            This is the exact payload that will be sent. It contains no account name,
            server name, database names, query text, or error messages — only the
            fields shown below.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-md border border-border bg-muted/40">
          <pre
            className="max-h-80 overflow-auto whitespace-pre-wrap break-words p-3 text-xs font-mono"
            data-testid="diag-send-preview-payload"
          >
            {redactError
              ? `Couldn't build preview: ${redactError}`
              : payload
                ? JSON.stringify(payload, null, 2)
                : 'Building preview…'}
          </pre>
        </div>
        {sendState === 'failed' && (
          <p className="text-sm text-destructive" data-testid="diag-send-preview-error">
            Sending failed. Please try again.
          </p>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={sendState === 'sending'}
            data-testid="diag-send-preview-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={sendState === 'sending' || !payload}
            data-testid="diag-send-confirm-btn"
          >
            {sendState === 'sending' ? 'Sending…' : 'Send'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
